<template>
    <NuxtLink
        v-if="grant"
        :to="localeRoute({
            name: 'grants-slug',
            params: {
                slug: grant.slug
            }
        })"
        class="sf-grant-card"
        :class="{
            'sf-grant-card--disabled': !nextDeadline
        }"
    >
        <div class="sf-grant-card__body">
            <h3 class="sf-grant-card__title">{{ grant.title }}</h3>

            <div class="sf-grant-card__meta">
                <template v-if="!nextDeadline">
                    <span class="sf-grant-card__tag sf-grant-card__tag--closed">
                        {{ t('deadline-closed') }}
                    </span>
                </template>

                <template v-else-if="nextDeadline && grant.maxApplicants">
                    <span class="sf-grant-card__tag">
                        {{ t('maxApplicants', { count: grant?.maxApplicants ?? 0 }) }}
                    </span>
                </template>

                <template v-else-if="nextDeadline && grant.type !== 'continuous'">
                    <span class="sf-grant-card__tag">
                        {{ nextDeadline }}
                    </span>
                </template>

                <template v-if="grant.extraText">
                    <span class="sf-grant-card__tag sf-grant-card__tag--extra">
                        {{ grant.extraText }}
                    </span>
                </template>

                <div class="grant-card__stickers">
                    <SfStickerNearlyClosed
                        v-if="grant.isNearlyClosed"
                        class="sf-sticker--small"
                    />
                    <SfStickerNearlyOpened
                        v-else-if="grant.isNearlyOpened"
                        class="sf-sticker--small"
                    />
                    <SfStickerNew
                        v-if="grant.isNew"
                        class="sf-sticker--small"
                    />
                </div>
            </div>
        </div>

        <div class="sf-grant-card__preview">
            <div
                v-if="randomImage && randomImage.url"
                class="sf-grant-card__image"
                :style="{
                    backgroundImage: `url('${ randomImage.url }')`
                }"
            />
        </div>

        <!-- eslint-disable -->
        <div
            v-if="grant.shortDescription"
            class="sf-grant-card__description"
            v-html="grant.shortDescription"
        />
        <!-- eslint-enable -->
    </NuxtLink>
</template>

<script setup>
import { parseISO } from 'date-fns';
import SfStickerNew from '~/patterns/atoms/sticker/presets/new.vue';
import SfStickerNearlyClosed from '~/patterns/atoms/sticker/presets/nearly-closed.vue';
import SfStickerNearlyOpened from '~/patterns/atoms/sticker/presets/nearly-opened.vue';

const { t, d } = useI18n({
    useScope: 'local'
});

const localeRoute = useLocaleRoute();

const props = defineProps({
    grant: {
        type: Object,
        required: true
    }
});

const { grant } = toRefs(props);

const nextDeadline = computed(() => {
    if (grant.value?.nextDeadline) {
        return t('deadline', { date: d(parseISO(grant.value.nextDeadline), 'dateShort') });
    }

    return false;
});

const randomImage = computed(() => grant.value?.heroImages?.[0] ?? null);
</script>

<style lang="less" src="./grant-card.less"></style>

<i18n lang="json">
{
    "nl": {
        "type.open-call": "Open oproep",
        "current-round-deadline": "open tot en met {date}",
        "deadline": "sluit op {date}",
        "deadline-closed": "nu gesloten",
        "maxApplicants": "sluit bij {count} aanvragen"
    },
    "en": {
        "type.open-call": "Open call",
        "current-round-deadline": "open until {date}",
        "deadline": "closes on {date}",
        "deadline-closed": "now closed",
        "maxApplicants": "closes at {count} applications"
    }
}
</i18n>
